<template>
  <div v-if="hasLanguagePermission" class="language-selector">
    <el-select
      v-model="selectedLanguageCode"
      :disabled="isLoading"
      :loading="isLoading"
      size="default"
      class="language-select"
      :aria-label="$t('language_selector') || 'Language'"
      filterable
      @change="handleLanguageChange"
    >
      <el-option
        v-for="lang in uniqueLanguages"
        :key="lang.id"
        :label="lang.lang_name"
        :value="lang.lang_code"
      />
    </el-select>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import { ElLoading } from 'element-plus'
import { checkPermission, setCookies } from '@/common/utils'
import Global from '@/common/global.service'
import LanguageService from '@/services/language.service'
import mutations from '@/store/mutation_types'
import actions from '@/store/action_types'

export default {
  name: 'LanguageSelector',

  data() {
    return {
      selectedLanguageCode: null,
      isLoading: false
    }
  },

  computed: {
    ...mapState({
      user: (state) => state.current.user,
      enterprise_permissions: (state) => state.current.enterprise_permissions,
      activeLanguages: (state) => state.active_languages
    }),
    uniqueLanguages() {
      const seen = new Set()

      return this.activeLanguages.filter(lang => {
        if (seen.has(lang.lang_code)) {
          return false
        }

        seen.add(lang.lang_code)
        return true
      })
    },

    /**
     * RBAC guard: both SYSTEM_LANGUAGE and USER_MANAGEMENT must have READ_WRITE access.
     */
    hasLanguagePermission() {
      if (!this.enterprise_permissions) return false
      return (
        checkPermission(this.enterprise_permissions, 'SYSTEM_LANGUAGE', 'READ_WRITE') &&
        checkPermission(this.enterprise_permissions, 'USER_MANAGEMENT', 'READ_WRITE')
      )
    }
  },

  watch: {
    /**
     * Re-initialise when the user object is resolved (e.g. after login or
     * store hydration from localStorage).
     */
    user: {
      immediate: true,
      handler(newUser) {
        if (newUser && this.hasLanguagePermission) {
          this.init()
        }
      }
    }
  },

  methods: {
    ...mapMutations([
      mutations.SET_LOCALIZATION,
      mutations.SET_ACTIVE_LANGUAGES,
      mutations.SET_USER_LANGUAGE_CODE
    ]),
    ...mapActions([actions.fetchActiveLanguages]),

    async init() {
      // Seed the selector with the persisted language from the sign-in response.
      this.selectedLanguageCode = this.user?.language_code ?? null

      // Apply direction immediately from the saved language so the page
      // doesn't flash LTR before the first change event fires.
      if (this.selectedLanguageCode) {
        this.applyTextDirection(this.selectedLanguageCode)
      }

      if (!this.activeLanguages?.length) {
        try {
          await this[actions.fetchActiveLanguages]()
        } catch (err) {
          this.$notify?.error({
            title: 'Language',
            message: err?.response?.data?.message || 'Failed to load languages.'
          })
        }
      }
    },

    /**
     * Sets document direction and lang attribute based on the language code.
     * Arabic (ar_*, ar-*) uses RTL; everything else uses LTR.
     */
    applyTextDirection(languageCode) {
      const locale = languageCode?.substring(0, 2).toLowerCase()
      const isRtl = locale === 'ar'
      document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr')
      document.documentElement.setAttribute('lang', locale || 'en')
    },

    /**
     * Sequential async handler – each step awaits the previous one so that
     * race conditions are structurally impossible:
     *
     *  1. Persist the new preference on the backend (PUT).
     *  2. Only if (1) succeeds, fetch the localisation strings (GET).
     *  3. Apply them to vue-i18n and Vuex (synchronous – no race).
     *  4. Update the stored user.language_code and cookies so that any
     *     app opened afterwards inherits the new language immediately.
     */
    async handleLanguageChange(newLanguageCode) {
      if (!newLanguageCode || newLanguageCode === this.user?.language_code) return

      const enterpriseId = window.hkConfigEnvs.VUE_APP_ENTERPRISE_IDENTIFIER
      const userId = this.user?.id

      this.isLoading = true
      const loadingInstance = ElLoading.service({
        lock: true,
        fullscreen: true,
      })

      try {
        // Step 1 – Persist user preference.
        await LanguageService.updateUserLanguage(enterpriseId, userId, newLanguageCode)

        // Step 2 – Fetch localisation strings for the new language.
        const { data: localizations } = await Global.getLocalization(newLanguageCode)

        // Step 3 – Apply localisation to vue-i18n (UI update).
        const locale = newLanguageCode.substring(0, 2)
        this.$i18n.setLocaleMessage(locale, localizations)
        this.$i18n.locale = locale

        // Step 3b – Flip document direction for RTL languages (e.g. Arabic).
        this.applyTextDirection(newLanguageCode)

        // Step 4 – Sync store and cookies so launched apps inherit the language.
        this[mutations.SET_LOCALIZATION](localizations)
        this[mutations.SET_USER_LANGUAGE_CODE](newLanguageCode)
        setCookies(false)
      } catch (err) {
        // Roll back the dropdown to the last known-good value on any failure.
        this.selectedLanguageCode = this.user?.language_code ?? this.selectedLanguageCode

        this.$notify?.error({
          title: 'Language',
          message: err?.response?.data?.message || 'Failed to update language.'
        })
      } finally {
        this.isLoading = false
        loadingInstance.close()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.language-selector {
  display: flex;
  align-items: center;

  .language-select {
    width: 150px;

    :deep(.el-input__wrapper) {
      background-color: transparent;
      box-shadow: none;
      border: 1px solid rgba(255, 255, 255, 0.4);
      border-radius: 4px;

      .el-input__inner {
        color: inherit;
        font-size: 14px;
      }
    }
  }
}
</style>
